import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type { KvsUebersicht } from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { inDieAblage } from '../../kopieren';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, PropertyGrid, StatusBadge,
  Vorbehalte, formatiereZahl, spalteFuer, type Spalte,
} from '../../muster';
import { kvsVorbehalte, typdatenblattText } from './datenregeln';
import './lueftung.css';

type Gruppe = KvsUebersicht['gruppen'][number];
type Register = KvsUebersicht['typdatenblaetter'][number]['register'][number];
type Anlage = KvsUebersicht['anlagen'][number];

/**
 * Die Kreislaufverbunde der Bowl - KVS1 bis KVS4.
 *
 * Ein Kreislaufverbund liegt QUER zu den Anlagen. Er verbindet mehrere Geräte,
 * und keines von ihnen „hat“ ihn: Das Abluftgerät entzieht der Abluft Wärme,
 * das Zuluftgerät gibt sie an die Außenluft ab, dazwischen läuft ein
 * Glykolkreis mit einer Pumpe. Auf der einzelnen Anlagenseite steht deshalb
 * nur der Ausschnitt, der diese Anlage betrifft - hier steht das Ganze.
 *
 * Warum das Bild keine Gerätezuordnung zeigt und warum alle Anlagen der Gruppe
 * angeschlossen heißen, steht als Vorbehalt unter dem Kopf (`kvsVorbehalte`).
 *
 * TV6d (TX-STANDARD 1.6): nach `seiten/lueftung/` umgezogen (Route bleibt
 * `lueftung/kvs`); die acht Farbliterale des Bilds sind Merkmale
 * (`lueftung.css`), die Zahlen deutsch über `formatiereZahl`.
 */
export function Kreislaufverbund() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { id } = useParams<{ id: string }>();

  const [daten, setDaten] = useState<KvsUebersicht | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [kopiert, setKopiert] = useState<string | null>(null);
  const [kopierfehler, setKopierfehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setDaten(await api.lueftungKvs(projekt.id));
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  }, [api, projekt.id]);

  useEffect(() => { void laden(); }, [laden]);

  // Fehlende Sammlungen sind kein Absturzgrund: Liefert die API eine Antwort ohne `gruppen`, zeigt die Seite ihren
  // Leerzustand (gemessen im Prüfstand, Paket U6b).
  const gruppen = daten?.gruppen ?? [];
  const anlagen = daten?.anlagen ?? [];
  const typdatenblaetter = daten?.typdatenblaetter ?? [];

  const registerspalten: Spalte<Register>[] = useMemo(() => [
    {
      schluessel: 'wrg', titel: 'WRG', art: 'status', breite: '110px',
      titelHinweis: 'Trennt echte Rückgewinnung von Klimatisierung',
      wert: (r) => (r.istWrg ? 'WRG' : ''),
      inhalt: (r) => (r.istWrg ? <StatusBadge zustand="gut" text="WRG" klein /> : null),
    },
    { schluessel: 'komponente', titel: 'Komponente', immerSichtbar: true, wert: (r) => r.komponente },
    { schluessel: 'luftart', titel: 'Luftart', wert: (r) => r.luftart },
    { schluessel: 'betrieb', titel: 'Betrieb', wert: (r) => r.betriebspunkt },
    { schluessel: 'leistung', titel: 'Leistung', ...spalteFuer('leistung_kw'), wert: (r) => r.nennleistungKw },
    {
      schluessel: 'luft', titel: 'Luft ein → aus', einheit: '°C', ausrichtung: 'rechts', wert: (r) => r.eintrittC,
      inhalt: (r) => `${formatiereZahl(r.eintrittC, 1)} → ${formatiereZahl(r.austrittC, 1)}`,
    },
    {
      schluessel: 'medium', titel: 'Medium', einheit: '°C', ausrichtung: 'rechts', wert: (r) => r.mediumTemp1C,
      inhalt: (r) => `${formatiereZahl(r.mediumTemp1C, 1)}/${formatiereZahl(r.mediumTemp2C, 1)}`
        + (r.glykolProzent ? ` · ${formatiereZahl(r.glykolProzent, 0)} % Glykol` : ''),
    },
  ], []);

  const anlagenspalten: Spalte<Anlage>[] = useMemo(() => [
    { schluessel: 'anlage', titel: 'Anlage', immerSichtbar: true, wert: (a) => a.anlage },
    { schluessel: 'geraetetyp', titel: 'Gerätetyp', wert: (a) => a.geraetebezeichnung },
    { schluessel: 'ort', titel: 'Ebene / Raum', wert: (a) => [a.ebene, a.raumnummer].filter(Boolean).join(' · ') },
    // Der Wert kommt als Text aus der Anlage; Zahl wird er nur, wenn er eine ist.
    {
      schluessel: 'zuluft', titel: 'Zuluft', ...spalteFuer('volumenstrom_luft'),
      wert: (a) => (a.zuluft !== null && a.zuluft.trim() !== '' && Number.isFinite(Number(a.zuluft)) ? Number(a.zuluft) : a.zuluft),
    },
    {
      schluessel: 'verweis', titel: 'Öffentlicher Verweis', sortierbar: false, breite: '240px',
      inhalt: (a) => (a.verweis ? (
        <span className="lue-verweis">
          <a href={a.verweis} target="_blank" rel="noreferrer">öffnen</a>
          <button
            type="button"
            className="m-knopf m-knopf-still"
            onClick={async () => {
              if (await inDieAblage(a.verweis!)) {
                setKopiert(a.id);
                setKopierfehler(null);
                window.setTimeout(() => setKopiert(null), 1500);
              } else {
                setKopierfehler('Der Verweis liess sich nicht kopieren - „öffnen“ zeigt ihn in der Adresszeile.');
              }
            }}
          >
            {kopiert === a.id ? 'kopiert' : 'kopieren'}
          </button>
        </span>
      ) : <span className="lue-leise">kein Signaturschlüssel konfiguriert</span>),
    },
  ], [kopiert]);

  const kopf = (
    <PageHeader
      titel="Kreislaufverbund der Bowl"
      beschreibung={'Ein Kreislaufverbund hat auf Geräteebene keinen Wirkungsgrad - er entsteht erst im Verbund zweier '
        + 'Geräte über den Glykolkreis. Genau deshalb standen die Bowl-Anlagen lange ohne WRG-Wert da: Ihre '
        + 'Einzeldatenblätter tragen dafür „Keine ErP“.'}
      meta={<Link to={`/projekte/${id}/lueftung`}>← Lüftungsbestand</Link>}
    />
  );

  if (fehler !== null) {
    return (
      <>
        {kopf}
        <ErrorState meldung="Die Kreislaufverbunde konnten nicht geladen werden." technisch={fehler} beiErneutVersuchen={() => void laden()} />
      </>
    );
  }

  if (!daten) {
    return (
      <>
        {kopf}
        <LoadingState text="Kreislaufverbunde werden geladen …" zeilen={6} spalten={4} />
      </>
    );
  }

  if (!gruppen.length) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Für dieses Modell ist kein Kreislaufverbundsystem hinterlegt."
          erklaerung={'Die Kreislaufverbunde entstehen aus den Typdatenblättern der Lüftungsgeräte. Ohne hinterlegte '
            + 'Herstellerdatei gibt es weder Plätze noch Rückwärmzahl.'}
          aktion={<button type="button" className="m-knopf m-knopf-haupt" onClick={() => void laden()}>Erneut nachsehen</button>}
        />
      </>
    );
  }

  return (
    <>
      {kopf}

      <Vorbehalte eintraege={kvsVorbehalte()} />

      <KpiReihe>
        <KpiCard titel="Systeme" wert={gruppen.length} />
        <KpiCard titel="Angeschlossene Anlagen" wert={anlagen.length} />
        <KpiCard titel="Typdatenblätter" wert={typdatenblaetter.length} />
      </KpiReihe>

      {gruppen.map((g) => {
        const winter = g.saisons.find((s) => s.saison === 'Winter');
        const sommer = g.saisons.find((s) => s.saison === 'Sommer');

        return (
          <ContentSection
            key={g.anlage}
            titel={g.anlage}
            meta={winter?.rueckwaermzahlTrockenPct != null
              ? <StatusBadge zustand="neutral" text={`${formatiereZahl(winter.rueckwaermzahlTrockenPct, 1)} % Rückwärmzahl`} />
              : undefined}
          >
            <PropertyGrid
              spalten={3}
              dicht
              eintraege={[
                { beschriftung: 'Ausrichtung', wert: winter?.ausrichtung ?? null },
                { beschriftung: 'Energiewirkungsgrad', wert: winter?.energiewirkungsgradPct ?? null, einheit: '%', nachkomma: 1, herkunft: 'daten' },
                { beschriftung: 'Luftmenge je Seite', wert: winter?.zuluftM3h ?? null, einheit: 'm³/h', herkunft: 'daten' },
                {
                  beschriftung: `Leistung Winter bei ${formatiereZahl(winter?.aussentempC, 1)} °C`,
                  wert: winter?.zuluftKw ?? null, einheit: 'kW', herkunft: 'daten',
                },
                {
                  beschriftung: `Leistung Sommer bei ${formatiereZahl(sommer?.aussentempC, 1)} °C`,
                  wert: sommer?.zuluftKw ?? null, einheit: 'kW', herkunft: 'daten',
                },
                { beschriftung: 'Solemenge', wert: winter?.zuluftSoleM3h ?? null, einheit: 'm³/h', nachkomma: 1, herkunft: 'daten' },
              ]}
            />

            <Instanzbild gruppe={g} />
          </ContentSection>
        );
      })}

      <ContentSection
        titel="Gerätetypen und Register"
        beschreibung={'Die Spalte WRG trennt Rückgewinnung von Klimatisierung. Beim 100.000er stehen 1.163 kW Change-Over '
          + 'gegen 700 kW echte Rückgewinnung - wer beides addiert, hält eine Kältemaschinenleistung für zurückgewonnene Wärme.'}
      >
        {typdatenblaetter.length === 0 && (
          <EmptyState
            klein
            titel="Zu diesem Kreislaufverbund liegt kein Typdatenblatt vor."
            erklaerung="Ohne Herstellerdatei gibt es weder Register noch Leistungen."
          />
        )}

        {typdatenblaetter.map((t) => (
          <DataTable
            key={t.angebotsnummer}
            name={`Register ${t.bezeichnung}`}
            beschreibung={typdatenblattText(t)}
            spalten={registerspalten}
            zeilen={t.register}
            schluessel={(r) => `${t.angebotsnummer}|${r.komponente}|${r.betriebspunkt}`}
            suche={false}
            spaltenwahl={false}
            dicht
            leer={<span>Das Typdatenblatt nennt keine Register.</span>}
          />
        ))}
      </ContentSection>

      <ContentSection
        titel="Angeschlossene Anlagen"
        beschreibung="Der Verweis führt ohne Anmeldung auf die Anlagenseite - für Fremdfirmen und für den QR-Code im Plan."
        meta={`${formatiereZahl(anlagen.length, 0)} Anlagen`}
        nackt
      >
        {kopierfehler && <ErrorState klein titel="Kopieren ging nicht." meldung={kopierfehler} />}
        <DataTable
          name="Angeschlossene Anlagen"
          spalten={anlagenspalten}
          zeilen={anlagen}
          schluessel={(a) => String(a.id)}
          ausfuhr
          ausfuhrDateiname="kvs-anlagen"
          leer={(
            <EmptyState
              titel="An diesen Kreislaufverbund ist noch keine Anlage angeschlossen."
              erklaerung="Die Zuordnung entsteht aus der Lüftungsaufnahme des Modells."
            />
          )}
        />
      </ContentSection>
    </>
  );
}

/** Das Schemabild einer KVS-Instanz: Zuluftplätze, Glykolkreis, Abluftplätze. Farben aus den Merkmalen (lueftung.css). */
function Instanzbild({ gruppe }: { gruppe: Gruppe }) {
  const zu = gruppe.geraeteplaetze.filter((p) => p.luftart === 'Zuluft');
  const ab = gruppe.geraeteplaetze.filter((p) => p.luftart === 'Abluft');
  const winter = gruppe.saisons.find((s) => s.saison === 'Winter');

  const reihen = Math.max(zu.length, ab.length, 1);
  const hoehe = reihen * 26 + 34;
  const breite = 660;
  const mitte = hoehe / 2 + 8;

  const kasten = (liste: typeof zu, x: number, seite: 'zu' | 'ab') => liste.map((p, i) => (
    <g key={p.geraeteslot} className={`lue-kvs-${seite}`}>
      <rect x={x} y={30 + i * 26} width={200} height={22} rx={3} />
      <text x={x + 7} y={45 + i * 26}>
        {p.geraeteslot} · {formatiereZahl(p.volumenstromM3h, 0)} m³/h · {formatiereZahl(p.leistungKw, 0)} kW
      </text>
    </g>
  ));

  return (
    <svg
      viewBox={`0 0 ${breite} ${hoehe}`}
      className="lue-kvs-bild"
      role="img"
      aria-label={`Aufbau ${gruppe.anlage}: Zuluft- und Abluftplätze, verbunden durch den Glykolkreis`}
    >
      <text x={100} y={16} className="lue-kvs-kopf">Zuluft - Außenluft wird erwärmt</text>
      <text x={330} y={16} className="lue-kvs-kopf lue-kvs-kreis-text">
        Glykolkreis {winter?.glykolProzent ? `${formatiereZahl(winter.glykolProzent, 0)} %` : ''}
      </text>
      <text x={560} y={16} className="lue-kvs-kopf">Abluft - Wärme wird entzogen</text>

      {kasten(zu, 0, 'zu')}
      {kasten(ab, 460, 'ab')}

      {/* Der Kreis, der beide Seiten verbindet. Ohne ihn wären es zwei Listen nebeneinander - und genau diese Verbindung
          IST die Wärmerückgewinnung. */}
      <line x1={206} y1={mitte} x2={454} y2={mitte} className="lue-kvs-kreis" />
      <polygon points={`206,${mitte} 216,${mitte - 5} 216,${mitte + 5}`} className="lue-kvs-pfeil" />
      {winter?.rueckwaermzahlTrockenPct != null && (
        <text x={330} y={mitte - 6} className="lue-kvs-kreis-text lue-kvs-wert">
          {formatiereZahl(winter.rueckwaermzahlTrockenPct, 1)} % Rückwärmzahl
        </text>
      )}
    </svg>
  );
}
